import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  ChevronLeft,
  Mail,
  Printer,
  User
} from 'lucide-react';
import { doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { useAuth } from '../../../context/AuthContext';
import { AppContext } from '../../../context/AppContext';
import { db } from '../../../firebase/config';
import { useCurrency } from '../../../utils/currencyUtils';

const PayslipDetail = () => {
  const { payslipId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  const { formatAmount } = useCurrency();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payslip, setPayslip] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [business, setBusiness] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      if (!activeBusiness) {
        setError('Select a business before viewing payslip details.');
        setLoading(false);
        return;
      }

      if (!payslipId) {
        setError('Payslip not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const payslipSnapshot = await getDoc(doc(db, 'payslips', payslipId));
        if (!payslipSnapshot.exists()) {
          setError('Payslip not found');
          return;
        }

        const payslipData = payslipSnapshot.data();
        setPayslip({
          id: payslipSnapshot.id,
          ...payslipData,
          paymentDate: payslipData.paymentDate?.toDate ? payslipData.paymentDate.toDate() : payslipData.paymentDate,
          payPeriod: {
            startDate: payslipData.payPeriod?.startDate?.toDate ? payslipData.payPeriod.startDate.toDate() : payslipData.payPeriod?.startDate,
            endDate: payslipData.payPeriod?.endDate?.toDate ? payslipData.payPeriod.endDate.toDate() : payslipData.payPeriod?.endDate
          }
        });

        if (payslipData.employeeId) {
          const employeeSnapshot = await getDoc(doc(db, 'teamMembers', payslipData.employeeId));
          if (employeeSnapshot.exists()) {
            setEmployee({ id: employeeSnapshot.id, ...employeeSnapshot.data() });
          }
        }

        const businessSnapshot = await getDoc(doc(db, 'businesses', activeBusiness.id));
        if (businessSnapshot.exists()) {
          setBusiness({ id: businessSnapshot.id, ...businessSnapshot.data() });
        }
      } catch (fetchError) {
        console.error('Error loading payslip detail:', fetchError);
        setError('Failed to load payslip details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeBusiness, currentUser, payslipId]);

  const formatDate = (value) => {
    if (!value) return 'Not set';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'Not set' : date.toLocaleDateString();
  };

  const handleMarkSent = async () => {
    if (!payslip) return;

    try {
      await updateDoc(doc(db, 'payslips', payslipId), {
        status: 'sent',
        sentAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      setPayslip((current) => ({ ...current, status: 'sent' }));
    } catch (updateError) {
      console.error('Error updating payslip status:', updateError);
      window.alert('Failed to update payslip status.');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 flex justify-center items-center min-h-[300px]">
        <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
      </div>
    );
  }

  if (error || !payslip) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <button
          onClick={() => navigate('/payroll/payslips')}
          className="text-[#190192] hover:text-[#2C0DB5] flex items-center mb-4"
        >
          <ChevronLeft size={16} className="mr-1" />
          Back to Payslips
        </button>
        <div className="bg-red-50 rounded-lg p-6 text-center">
          <AlertCircle size={40} className="mx-auto text-red-500 mb-3" />
          <p className="text-red-800">{error || 'Payslip not found'}</p>
        </div>
      </div>
    );
  }

  const totalEmployerContributions = (payslip.employerContributions || []).reduce(
    (sum, item) => sum + Number(item.amount || 0),
    0
  );

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <button
          onClick={() => navigate('/payroll/payslips')}
          className="text-[#190192] hover:text-[#2C0DB5] flex items-center"
        >
          <ChevronLeft size={16} className="mr-1" />
          Back to Payslips
        </button>
        <h1 className="mt-2 text-xl font-bold text-gray-900">Payslip</h1>
        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            payslip.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
          }`}>
            {payslip.status || 'draft'}
          </span>
          <span className="flex items-center">
            <Calendar size={14} className="mr-1" />
            Payment date: {formatDate(payslip.paymentDate)}
          </span>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <div className="flex flex-wrap gap-3 justify-end">
          <button
            onClick={() => window.print()}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 inline-flex items-center"
          >
            <Printer size={16} className="mr-2" />
            Print
          </button>
          {employee?.email && (
            <button
              onClick={handleMarkSent}
              className="px-4 py-2 bg-[#190192] text-white rounded-lg hover:bg-[#2C0DB5] inline-flex items-center"
            >
              <Mail size={16} className="mr-2" />
              Mark as Sent
            </button>
          )}
          {payslip.status !== 'sent' && (
            <button
              onClick={handleMarkSent}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 inline-flex items-center"
            >
              <CheckCircle size={16} className="mr-2" />
              Set Sent
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Worker</h2>
            {employee ? (
              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-700 mr-3">
                    {employee.firstName?.charAt(0) || ''}
                    {employee.lastName?.charAt(0) || ''}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{employee.firstName} {employee.lastName}</p>
                    <p className="text-sm text-gray-500">{employee.position || 'Worker'}</p>
                  </div>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  {employee.email && <p>{employee.email}</p>}
                  <p className="flex items-center"><User size={14} className="mr-2 text-gray-400" />{employee.id}</p>
                </div>
              </div>
            ) : (
              <p className="text-gray-500">Worker details unavailable.</p>
            )}
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Business</h2>
            <div className="space-y-2 text-sm text-gray-600">
              <p className="font-medium text-gray-900">{business?.name || activeBusiness?.name || 'Business'}</p>
              {business?.address && <p>{business.address}</p>}
              <p>Pay period: {formatDate(payslip.payPeriod?.startDate)} - {formatDate(payslip.payPeriod?.endDate)}</p>
              <p>Employment type: <span className="capitalize">{payslip.employmentType || 'fullTime'}</span></p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Earnings and Deductions</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="font-medium text-gray-900 mb-3">Earnings</h3>
              <div className="space-y-2">
                {(payslip.earnings || []).map((earning, index) => (
                  <div key={`${earning.description}-${index}`} className="flex justify-between py-2 border-b border-gray-100 text-sm">
                    <span className="text-gray-700">{earning.description}</span>
                    <span className="font-medium text-gray-900">{formatAmount(earning.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between py-2 font-medium">
                  <span>Gross earnings</span>
                  <span>{formatAmount(payslip.grossAmount)}</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-medium text-gray-900 mb-3">Deductions and Benefits</h3>
              <div className="space-y-2">
                {(payslip.deductions || []).map((item, index) => (
                  <div key={`${item.name}-${index}`} className="flex justify-between py-2 border-b border-gray-100 text-sm">
                    <span className="text-gray-700">{item.name}</span>
                    <span className="font-medium text-gray-900">-{formatAmount(item.amount)}</span>
                  </div>
                ))}
                {(payslip.benefits || []).map((item, index) => (
                  <div key={`${item.name}-${index}`} className="flex justify-between py-2 border-b border-gray-100 text-sm">
                    <span className="text-gray-700">{item.name}</span>
                    <span className="font-medium text-gray-900">+{formatAmount(item.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between py-2 font-medium">
                  <span>Net pay</span>
                  <span className="text-[#190192]">{formatAmount(payslip.netAmount)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Employer Contributions and YTD</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-500">Employer Contributions</p>
              <p className="text-xl font-semibold text-gray-900">{formatAmount(totalEmployerContributions)}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-500">YTD Earnings</p>
              <p className="text-xl font-semibold text-gray-900">{formatAmount(payslip.ytdEarnings || 0)}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-500">YTD PAYE</p>
              <p className="text-xl font-semibold text-gray-900">{formatAmount(payslip.ytdTaxes || 0)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayslipDetail;
